import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Table from "../commonComponent/Table";
import useLoader from "../../hooks/useLoader";
import useModal from "../../hooks/useModal";
import ViewModal from "../commonComponent/ViewModal";

const API_URL = process.env.REACT_APP_API_URL;

const NewsComments = () => {
    const [data, setData] = useState([]);
    const [error, setError] = useState(null);
    const [confirmModalVisible, setConfirmModalVisible] = useState(false); // Modal visibility
    const [selectedRow, setSelectedRow] = useState(null); // Selected row for confirmation
    const [actionType, setActionType] = useState(""); // Action type (approve/reject)
    const { loading, showLoader, hideLoader } = useLoader();
    const {
        showViewModal,
        selectedItem,
        handleOpenViewModal,
        handleCloseViewModal,
    } = useModal();

    // Function to show the confirmation modal with the action type
    const handleShowConfirmModal = (row, action) => {
        setSelectedRow(row);
        setActionType(action);
        setConfirmModalVisible(true);
    };

    // Function to handle the confirmation (Approve/Reject)
    const handleConfirmAction = async () => {
        if (!selectedRow || !actionType) return;

        const { _id } = selectedRow;

        try {
            const response = await fetch(`${API_URL}/video/updatecomment/${_id}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ verified: actionType.toLowerCase() }),
            });

            if (!response.ok) {
                throw new Error("Failed to update comment status");
            }

            // Update the state with the new verified status
            const updatedData = data.map((comment) =>
                comment._id === _id ? { ...comment, verified: actionType.toLowerCase() } : comment
            );
            setData(updatedData);

            toast.success(`Comment ${actionType.toLowerCase()}d successfully`);
        } catch (error) {
            console.error("Error updating status:", error);
            toast.error("Error updating status: " + error.message);
        } finally {
            setConfirmModalVisible(false); // Close modal after action
        }
    };
    const formatDate = (dateString) => {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', {
        weekday: 'long',
        month: 'long',
        day: '2-digit',
        year: 'numeric',
      }).format(date);
    };
   
  const columns = [
    { header: "Name", accessor: "name" },
    { header: "Type", accessor: "type" },
    { header: "Date", accessor: "createdAt", render: (rowData) => formatDate(rowData.createdAt) },  

    {
      header: "Status",
      accessor: "verified",
      render: (row) => {
        let bgColor;
        let text;

        switch (row.verified) {
          case "approved":
            bgColor = "green";
            text = "Approved";
            break;
          case "rejected":
            bgColor = "red";
            text = "Rejected";
            break;
          case "pending":
            bgColor = "orange";
            text = "Pending";
            break;
          default:
            bgColor = "gray";
            text = "Unknown";
        }

        return (
            <span
            style={{
              backgroundColor: bgColor,
              color: "white",
              padding: "2px  10px",
              borderRadius: "20px", // This makes it rounded
              fontSize: "10px", // Adjust the font size for readability
              display: "inline-block", // To ensure it takes the shape of the text
              textAlign: "center", // Align text to the center
            }}
          >
            {text}
          </span>
        );
      },
    },
    { header: "Comment", accessor: "comment" },
    {
      header: "",
      accessor: "actions",
      render: (row) => {
        const isApproved = row.verified === "approved";
        const isRejected = row.verified === "rejected";

        return (
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
           

            {!isApproved && !isRejected && (
              <>
                <button
                  onClick={() => handleShowConfirmModal(row, "approved")}
                  className="btn btn-success"
                  style={{
                    backgroundColor: "green",
                    color: "white",
                    padding: "8px 16px",
                    borderRadius: "5px",
                    cursor: "pointer",
                    fontSize: "14px",
                  }}
                >
                  Approve
                </button>
                <button
                  onClick={() => handleShowConfirmModal(row, "rejected")}
                  className="btn btn-danger"
                  style={{
                    backgroundColor: "red",
                    color: "white",
                    padding: "8px 16px",
                    borderRadius: "5px",
                    cursor: "pointer",
                    fontSize: "14px",
                  }}
                >
                  Reject
                </button>
              </>
            )}
            {isApproved && (
              <button
                onClick={() => handleShowConfirmModal(row, "rejected")}
                className="btn btn-danger"
                style={{
                  backgroundColor: "red",
                  color: "white",
                  padding: "8px 16px",
                  borderRadius: "5px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Reject
              </button>
            )}
            {isRejected && (
              <button
                onClick={() => handleShowConfirmModal(row, "approved")}
                className="btn btn-success"
                style={{
                  backgroundColor: "green",
                  color: "white",
                  padding: "8px 16px",
                  borderRadius: "5px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Approve
              </button>
            )}
          </div>
        );
      },
    },
  ];


    useEffect(() => {
        const fetchComments = async () => {
            showLoader();
            try {
                const response = await fetch(`${API_URL}/video/getAllComments`);
                if (!response.ok) {
                    throw new Error("Failed to fetch comments");
                }

                const result = await response.json();
                const { comments } = result;

                setData(comments);
            } catch (error) {
                setError(error);
                toast.error("Error fetching comments: " + error.message);
            } finally {
                hideLoader();
            }
        };

        fetchComments();
    }, []);

    const fieldsToShow = {
        Name: "name",
        Comment: "comment",
        Type:"type"
    };
    return (
        <div>
            <h2 className="capitalize font-bold text-[24px] mb-4">Comments</h2>

            {error && <div className="error-message">{error.message}</div>}

            <div className="table-container">
                <Table
                    columns={columns}
                    data={data}
                    showIndex={true}
                    loading={loading}
                    onView={(row) => handleOpenViewModal(row)} // Pass the entire row data to handleOpenViewModal
                    actions={{
                        showView: true,
                    }}
                />

            </div>

            <ViewModal
                show={showViewModal}
                onHide={handleCloseViewModal}
                data={selectedItem}  // This should now contain the selected row's full data
                fields={fieldsToShow}
                renderField={(fieldName, value) =>
                    fieldName === "Comment" ? (
                        <pre style={{ whiteSpace: "pre-wrap" }}>{value}</pre>
                    ) : fieldName === "Name" ? (
                        <span>{value}</span>
                    ) : (
                        <span>{value}</span>  // This will render other fields as well
                    )
                }
            />


            {/* Confirmation Modal for Approve/Reject */}
            {confirmModalVisible && (
                <div
                    style={{
                        position: "fixed",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        backgroundColor: "white",
                        borderRadius: "8px",
                        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.25)",
                        padding: "20px",
                        zIndex: 1000,
                        width: "400px",
                    }}
                >
                    <h3>{actionType} Confirmation</h3>
                    <p>
                        Are you sure you want to {actionType.toLowerCase()} the following
                        comment?
                    </p>
                    <p>
                        <strong>Name:</strong> {selectedRow?.name}
                    </p>
                    <p>
                        <strong>Comment:</strong> {selectedRow?.comment}
                    </p>
                    <div style={{ marginTop: "20px", textAlign: "center" }}>
                        <button
                            onClick={handleConfirmAction}
                            style={{
                                backgroundColor: actionType === "Approve" ? "green" : "red",
                                color: "white",
                                padding: "10px 20px",
                                marginRight: "10px",
                                border: "none",
                                borderRadius: "5px",
                                cursor: "pointer",
                            }}
                        >
                            Confirm
                        </button>
                        <button
                            onClick={() => setConfirmModalVisible(false)} // Close modal
                            style={{
                                backgroundColor: "#ccc",
                                color: "black",
                                padding: "10px 20px",
                                border: "none",
                                borderRadius: "5px",
                                cursor: "pointer",
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NewsComments;
